<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { computed, useId } from 'vue';
  import {
    descriptionClass,
    itemClass,
    itemStatusClasses,
    labelClass,
    leadingClass,
    listClass,
    rootClass,
    visuallyHiddenClass,
  } from './RequirementsGroup.css';
  import type {
    RequirementsGroupItem,
    RequirementsGroupItemStatusType,
    RequirementsGroupProps,
  } from './RequirementsGroup.types';
  import { RequirementsGroupItemStatus } from './RequirementsGroup.types';

  const { t } = useLocale('ds-components');

  const props = defineProps<RequirementsGroupProps>();

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-requirements-group-${uid}`,
  );

  const statusOf = (item: RequirementsGroupItem) =>
    item.status ?? RequirementsGroupItemStatus.Default;

  const statusAnnouncement = (
    item: RequirementsGroupItem,
    status: RequirementsGroupItemStatusType,
  ) => {
    switch (status) {
      case RequirementsGroupItemStatus.Fulfilled:
        return t('requirements_group.status_fulfilled', {
          description: item.description,
        });
      case RequirementsGroupItemStatus.Error:
        return t('requirements_group.status_error', {
          description: item.description,
        });
      default:
        return undefined;
    }
  };

  const resolvedItems = computed(() =>
    props.items.map((item) => {
      const status = statusOf(item);
      return {
        ...item,
        status,
        announcement: statusAnnouncement(item, status),
      };
    }),
  );
</script>

<template>
  <div :class="rootClass" :data-testid="resolvedTestId">
    <span
      :id="`${uid}-label`"
      :class="labelClass"
      :data-testid="`${resolvedTestId}-label`"
      :title="label"
      >{{
        label
      }}</span
    >
    <!-- biome-ignore lint/a11y/noRedundantRoles: list-style:none makes Safari/VoiceOver drop list semantics; the explicit role restores them -->
    <ul :class="listClass" role="list" :aria-labelledby="`${uid}-label`">
      <li
        v-for="(item, index) in resolvedItems"
        :key="index"
        :class="[itemClass, itemStatusClasses[item.status]]"
        :data-status="item.status"
        :data-testid="`${resolvedTestId}-item-${index}`"
      >
        <span :class="leadingClass" :title="item.leading">{{
          item.leading
        }}</span>
        <span :class="descriptionClass" :title="item.description">{{
          item.description
        }}</span>
      </li>
    </ul>
    <div
      :class="visuallyHiddenClass"
      role="status"
      aria-live="polite"
      :data-testid="`${resolvedTestId}-status`"
    >
      <span v-for="(item, index) in resolvedItems" :key="index">{{
        item.announcement
      }}</span>
    </div>
  </div>
</template>
